import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const steps = [
  {
    number: '01',
    title: 'Opprett konto',
    description: 'Registrer deg gratis på under 2 minutter. Ingen kredittkort nødvendig for gratis-planen. Fyll inn navn og e-post — du er i gang.',
    icon: 'UserPlus',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19241d844-1772187879311.png',
    imageAlt: 'Person using smartphone to register for NordPass account on a Norwegian mountain road',
    color: '#3B6FD4',
    note: '< 2 min'
  },
  {
    number: '02',
    title: 'Legg til kjøretøy',
    description: 'Registrer bilen din med registreringsnummer og utslippsklasse. Vi beregner automatisk riktige rabatter for AutoPASS og elbil-fordeler.',
    icon: 'Car',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d5cad097-1772187878520.png',
    imageAlt: 'Norwegian electric car being registered in the NordPass vehicle management system',
    color: '#2EC4A0',
    note: 'AutoPASS-støtte'
  },
  {
    number: '03',
    title: 'Utforsk veikart',
    description: 'Åpne det interaktive veikartet og se sanntids trafikk, vegmeldinger og kamerafeed fra hele Norge. Planlegg ruten din med full oversikt.',
    icon: 'Map',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_163ec8874-1772187879963.png',
    imageAlt: 'Interactive Norwegian road map displayed on tablet showing traffic conditions and toll stations',
    color: '#E8941A',
    note: 'Oppdateres hvert min'
  },
  {
    number: '04',
    title: 'Beregn bomkostnader',
    description: 'Skriv inn start og mål — kalkulatoren viser eksakte bomkostnader, alternative ruter og total reisetid. Spar penger med smarte rutevalg.',
    icon: 'Calculator',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_151e03a86-1772187880729.png',
    imageAlt: 'Driver using NordPass toll calculator on phone while planning route through Norwegian mountains',
    color: '#6B5CE7',
    note: 'Spar opp til 40%'
  }
];

const HowItWorksSection = () => {
  return (
    <section style={{ background: '#0D1929', padding: '100px 0 120px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '80px', flexWrap: 'wrap', gap: '24px' }}>
          <div>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.6rem',
              letterSpacing: '0.3em', color: 'rgba(59,111,212,0.6)',
              textTransform: 'uppercase', marginBottom: '14px'
            }}>06 — Slik fungerer det</div>
            <h2 style={{
              fontFamily: '"Arial Black", Impact, sans-serif',
              fontSize: 'clamp(2.5rem, 5.5vw, 5rem)',
              fontWeight: 900, color: '#EEF0F2',
              letterSpacing: '-0.04em', lineHeight: 0.9,
              textTransform: 'uppercase', margin: 0
            }}>FRA REGISTRERING<br /><span style={{
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 'clamp(1.8rem, 3.5vw, 3.5rem)',
              color: '#3B6FD4',
              textTransform: 'none',
              letterSpacing: '-0.02em'
            }}>til veien på 4 steg</span></h2>
          </div>
          <Link
            to="/login"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              padding: '14px 28px',
              background: 'transparent',
              border: '1px solid rgba(59,111,212,0.4)',
              color: '#3B6FD4',
              fontFamily: '"Arial Black", sans-serif',
              fontSize: '0.65rem', fontWeight: 900,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              textDecoration: 'none', transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59,111,212,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Kom i gang nå
            <Icon name="ArrowRight" size={14} color="#3B6FD4" />
          </Link>
        </div>

        {/* Steps — alternating, organic */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {steps?.map((step, i) => (
            <div
              key={i}
              className="how-step"
              style={{
                display: 'grid',
                gridTemplateColumns: i % 2 === 0 ? '1fr 1.4fr' : '1.4fr 1fr',
                gap: '2px', minHeight: '300px'
              }}
            >
              {/* Content block */}
              <div
                style={{
                  order: i % 2 === 0 ? 1 : 2,
                  background: '#111620',
                  padding: '40px 32px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  position: 'relative', overflow: 'hidden'
                }}
              >
                {/* Ghost number */}
                <div style={{
                  position: 'absolute',
                  right: i % 2 === 0 ? '-10px' : 'auto',
                  left: i % 2 !== 0 ? '-10px' : 'auto',
                  bottom: '-20px',
                  fontFamily: '"Arial Black", Impact, sans-serif',
                  fontSize: '10rem', fontWeight: 900,
                  color: `${step?.color}08`,
                  letterSpacing: '-0.08em', lineHeight: 1,
                  pointerEvents: 'none', userSelect: 'none'
                }}>{step?.number}</div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  {/* Step number + icon */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                    <div style={{
                      fontFamily: 'monospace', fontSize: '0.6rem',
                      letterSpacing: '0.2em', color: `${step?.color}80`,
                      textTransform: 'uppercase'
                    }}>Steg {step?.number}</div>
                    <div style={{
                      width: '32px', height: '32px',
                      background: `${step?.color}15`,
                      border: `1px solid ${step?.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Icon name={step?.icon} size={14} color={step?.color} />
                    </div>
                    {/* Inline note — annotation style */}
                    <div style={{
                      fontFamily: 'Georgia, serif', fontStyle: 'italic',
                      fontSize: '0.7rem', color: `${step?.color}60`,
                      border: `1px solid ${step?.color}20`,
                      padding: '2px 8px',
                      transform: 'rotate(-1deg)'
                    }}>{step?.note}</div>
                  </div>

                  {/* Title — uppercase sans */}
                  <h3 style={{
                    fontFamily: '"Arial Black", Impact, sans-serif',
                    fontSize: 'clamp(1.5rem, 2.5vw, 2.2rem)',
                    fontWeight: 900, color: '#EEF0F2',
                    letterSpacing: '-0.03em', lineHeight: 0.95,
                    textTransform: 'uppercase', margin: '0 0 16px'
                  }}>{step?.title}</h3>

                  {/* Description — italic serif */}
                  <p style={{
                    fontFamily: 'Georgia, serif',
                    fontSize: '0.9375rem', color: 'rgba(238,240,242,0.6)',
                    lineHeight: 1.75, margin: 0, maxWidth: '380px'
                  }}>{step?.description}</p>
                </div>
              </div>

              {/* Image block */}
              <div
                className="how-step-image"
                style={{
                  order: i % 2 === 0 ? 2 : 1,
                  overflow: 'hidden', position: 'relative',
                  minHeight: '200px'
                }}
              >
                <img
                  src={step?.image}
                  alt={step?.imageAlt}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    filter: 'brightness(0.6) contrast(1.1) saturate(0.75)',
                    transition: 'transform 600ms ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                />
                {/* Color overlay */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: i % 2 === 0
                    ? `linear-gradient(to left, transparent 50%, rgba(17,22,32,0.5) 100%)`
                    : `linear-gradient(to right, transparent 50%, rgba(17,22,32,0.5) 100%)`
                }} />
                {/* Step color accent */}
                <div style={{
                  position: 'absolute',
                  bottom: 0, left: 0, right: 0,
                  height: '3px',
                  background: step?.color
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .how-step { grid-template-columns: 1fr !important; }
          .how-step > div { order: unset !important; }
          .how-step-image { min-height: 200px !important; }
        }
      `}</style>
    </section>
  );
};

export default HowItWorksSection;